import React, { useState } from "react"

import { useQuery } from "@tanstack/react-query"

import Icon from "@/components/Icon"
import DrivingLicenceSection from "@/components/applicants/DrivingLicenceSection"
import UploadDocumentDialog from "@/components/people/dialogs/UploadDocumentDialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { supabase } from "@/lib/supabase"
import type { DriverLicenceRecord } from "@/types/driverLicence"
import { Person } from "@/types/person"
import { SIGNED_URL_EXPIRY, createSignedDocumentUrl } from "@/utils/storageUtils"

interface DocumentsTabProps {
  person: Person
}

interface DocumentPreview {
  type: string
  url: string | undefined
}

const SECTION_DOCUMENT_TYPES = {
  "driving-licence": [
    { value: "dvla-front", label: "DVLA License (Front)" },
    { value: "dvla-back", label: "DVLA License (Back)" },
    { value: "cpc-front", label: "CPC Card (Front)" },
    { value: "cpc-back", label: "CPC Card (Back)" },
    { value: "tacho-card-front", label: "Tachograph Card (Front)" },
    { value: "tacho-card-back", label: "Tachograph Card (Back)" },
    { value: "d4-medical", label: "D4 Medical Report" },
  ],
  "identity-rtw": [
    { value: "rtw-document", label: "Right to Work Document" },
    { value: "bank-proof", label: "Bank Proof" },
  ],
  insurance: [{ value: "insurance-proof", label: "Insurance Proof" }],
  contracts: [
    { value: "vehicle-release-form", label: "Vehicle Release Form" },
    { value: "driver-contract", label: "Contract" },
  ],
} as const

type SectionKey = keyof typeof SECTION_DOCUMENT_TYPES

const SECTIONS: { title: string; key: SectionKey }[] = [
  { title: "Driving Licence Documents", key: "driving-licence" },
  { title: "Identity & Right to Work", key: "identity-rtw" },
  { title: "Insurance Documents", key: "insurance" },
  { title: "Contracts", key: "contracts" },
]

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return "N/A"
  const date = new Date(dateString)
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}

const getStatusBadge = (status: string | undefined) => {
  if (!status) return null

  const statusConfig: Record<
    string,
    { variant: string; icon: React.ReactNode; className: string }
  > = {
    approved: {
      variant: "default",
      icon: <Icon name="checkCircle" className="h-3 w-3 text-green-800" />,
      className: "bg-green-100 text-green-800 hover:bg-green-100",
    },
    pending: {
      variant: "secondary",
      icon: <Icon name="clock" className="h-3 w-3 text-yellow-800" />,
      className: "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
    },
    rejected: {
      variant: "destructive",
      icon: <Icon name="xCircle" className="h-3 w-3 text-red-800" />,
      className: "bg-red-100 text-red-800 hover:bg-red-100",
    },
  }

  const config = statusConfig[status.toLowerCase()] || statusConfig["pending"]

  return (
    <Badge className={`flex items-center gap-1 ${config.className}`}>
      {config.icon}
      <span className="capitalize">{status}</span>
    </Badge>
  )
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({ person }) => {
  const [previewDoc, setPreviewDoc] = useState<DocumentPreview | null>(null)
  const [uploadSection, setUploadSection] = useState<SectionKey | null>(null)

  const { data: documents } = useQuery({
    queryKey: ["person-documents", person.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .eq("person_id", person.id)
        .order("created_at", { ascending: false, nullsFirst: false })

      if (error) throw error

      const docs = data || []
      // Documents held on the person row predate the `documents` table, so they
      // carry no status or upload time.
      const legacyEntries = [
        { type: "bank-proof", file: person.bank_proof },
        { type: "vehicle-release-form", file: person.vehicle_release_form },
        { type: "driver-contract", file: typeof person.contract === "string" ? person.contract : null },
      ]
        .filter((entry) => entry.file && !docs.some((d) => d.type === entry.type))
        .map((entry) => ({
          ...entry,
          file: entry.file as string,
          status: null,
          created_at: null,
        }))

      return Promise.all(
        [...docs, ...legacyEntries].map(async (doc) => {
          if (!doc.file) {
            return doc
          }
          const signedUrl = await createSignedDocumentUrl(doc.file)
          return { ...doc, file: signedUrl || doc.file }
        }),
      )
    },
    staleTime: SIGNED_URL_EXPIRY * 1000,
  })

  // Fetch driver licence records for this person
  const { data: driverLicenceRecords } = useQuery({
    queryKey: ["person-driver-licence-records", person.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("driver_licence_records")
        .select("*")
        .eq("people_id", person.id)
        .order("created_at", { ascending: false })

      if (error) throw error
      return data as DriverLicenceRecord[]
    },
  })

  const getDocumentData = (type: string) => {
    const doc = documents?.find((d) => d.type === type)
    if (!doc) return null
    return {
      url: doc.file || undefined,
      status: doc.status || undefined,
      uploadedAt: doc.created_at || undefined,
    }
  }

  const existingDocumentTypes = (documents?.map((d) => d.type).filter(Boolean) as string[]) || []

  const renderDocumentCard = (
    type: string,
    doc: { url?: string; status?: string; uploadedAt?: string } | null | undefined,
  ) => {
    const url = doc?.url
    const status = doc?.status
    const uploadedAt = doc?.uploadedAt
    const isPdf = url ? url.split("?")[0].toLowerCase().endsWith(".pdf") : false

    return (
      <Dialog
        key={type}
        open={previewDoc?.type === type}
        onOpenChange={(open) => setPreviewDoc(open ? { type, url } : null)}
      >
        <DialogTrigger asChild>
          <div
            className="bg-white rounded-lg shadow p-4 flex flex-col items-center relative cursor-pointer hover:ring-2 hover:ring-blue-200 transition"
            onClick={() => setPreviewDoc({ type, url })}
          >
            {url ? (
              isPdf ? (
                <div className="w-24 h-24 border rounded flex flex-col items-center justify-center bg-palette-jungleGreen000">
                  <Icon name="file" className="w-12 h-12 text-red-500 mb-1" />
                  <p className="text-xs text-palette-jungleGreen700 font-medium">PDF Document</p>
                </div>
              ) : (
                <img
                  src={url}
                  alt={type}
                  className={`w-24 h-24 object-contain mb-2 border rounded`}
                />
              )
            ) : (
              <div className="w-24 h-24 border rounded flex items-center justify-center bg-palette-jungleGreen000">
                <p className="text-palette-jungleGreen500 text-center text-xs">Not available</p>
              </div>
            )}
            <div className="font-semibold text-palette-jungleGreen800 mb-1 mt-2 text-center">
              {type}
            </div>
            {status && <div className="mt-2">{getStatusBadge(status)}</div>}
            {uploadedAt && (
              <div className="flex items-center gap-1 text-xs text-palette-jungleGreen500 mt-1">
                <Icon name="calendarBlank" className="h-3 w-3 text-palette-jungleGreen500" />
                <span>{formatDate(uploadedAt)}</span>
              </div>
            )}
          </div>
        </DialogTrigger>
        <DialogContent className={isPdf ? "max-w-4xl" : ""}>
          <DialogHeader>
            <DialogTitle>{type}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4">
            {url ? (
              isPdf ? (
                <div className="w-full h-[70vh] border rounded">
                  <iframe
                    src={url}
                    title={type}
                    className="w-full h-full"
                    style={{ border: "none" }}
                  />
                </div>
              ) : (
                <img src={url} alt={type} className="object-contain border rounded" />
              )
            ) : (
              <div className="w-64 h-40 border rounded flex items-center justify-center bg-palette-jungleGreen000">
                <p className="text-palette-jungleGreen500">Document not available</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setPreviewDoc(null)
                }}
              >
                Close
              </Button>
              {url && isPdf && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    window.open(url, "_blank")
                  }}
                >
                  Open in New Tab
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <div className="space-y-6">
      {driverLicenceRecords && driverLicenceRecords.length > 0 && (
        <div className="mb-6">
          <DrivingLicenceSection driverLicenceRecords={driverLicenceRecords} />
        </div>
      )}

      {SECTIONS.map((section) => (
        <div key={section.key} className="border rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">{section.title}</h3>
            <Button variant="outline" size="sm" onClick={() => setUploadSection(section.key)}>
              <Icon name="export" className="h-4 w-4 mr-2 text-palette-jungleGreen900" />
              Upload documents
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SECTION_DOCUMENT_TYPES[section.key].map((dt) => {
              const docData = getDocumentData(dt.value)
              return renderDocumentCard(dt.label, docData)
            })}
          </div>
        </div>
      ))}

      {uploadSection && (
        <UploadDocumentDialog
          personId={person.id}
          documentTypes={[...SECTION_DOCUMENT_TYPES[uploadSection]]}
          existingDocumentTypes={existingDocumentTypes}
          open={true}
          onOpenChange={(open) => {
            if (!open) setUploadSection(null)
          }}
        />
      )}
    </div>
  )
}

export default DocumentsTab
